import React, { useEffect, useMemo, useState } from 'react';
import './SubscriptionPage.scss';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import BreadCrumbs from '../../components/BreadCrumbs/BreadCrumbs';
import { useService } from 'react-service-locator';
import { UserService, SubscriptionInfoResponse } from '../../services/user.service';
import CancelSubscriptionModal from './CancelSubscriptionModal';

const SubscriptionPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const history = useHistory();
    const userService = useService(UserService);

    const [loading, setLoading] = useState(true);
    const [sub, setSub] = useState<SubscriptionInfoResponse | null>(null);

    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const getErr = (e: any) => e?.response?.data?.error ?? e?.message ?? 'Error';

    const load = async () => {
        setLoading(true);
        try {
            const res = await userService.getSubscription();
            setSub(res);
        } catch (e: any) {
            toast.error(getErr(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const isDemo = sub?.status === 'demo' || sub?.canChooseLicense;

    const locale = useMemo(() => (i18n.language?.startsWith('nl') ? 'nl-NL' : 'en-US'), [i18n.language]);

    const formattedEndDate = useMemo(() => {
        if (!sub?.periodEndUtc) return '';
        const dt = new Date(sub.periodEndUtc);
        return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long', year: 'numeric' }).format(dt);
    }, [sub?.periodEndUtc, locale]);

    const licenseType = useMemo(() => (localStorage.getItem('licenseType') || '').toLowerCase(), []);

    const planTitle = useMemo(() => {
        if (isDemo) return t('pages.subscription.demo.title', { defaultValue: 'Demo account' });
        if (licenseType === 'smallorg') return t('pages.license.smallOrg.title');
        if (licenseType === 'basic') return t('pages.license.basic.title');
        return t('pages.subscription.plan.unknownTitle', { defaultValue: t('components.navbar.subscription') });
    }, [licenseType, isDemo, t]);

    const planDescription = useMemo(() => {
        if (isDemo)
            return t('pages.subscription.demo.desc', {
                defaultValue: 'Een individueel account voor demo doeleinden.',
            });
        if (licenseType === 'smallorg') return t('pages.license.smallOrg.desc', { defaultValue: '' });
        if (licenseType === 'basic') return t('pages.license.basic.desc', { defaultValue: '' });
        return '';
    }, [licenseType, isDemo, t]);

    const priceNode = useMemo(() => {
        if (isDemo) return null;

        if (licenseType === 'smallorg') {
            return (
                <div className="priceInline">
                    <span className="amount">{t('pages.license.smallOrg.price.base')}</span>
                    <span className="per">{t('pages.license.common.perYear')}</span>
                </div>
            );
        }

        return (
            <div className="priceInline">
                <span className="amount">{t('pages.license.basic.price.amount')}</span>
                <span className="per">{t('pages.license.common.perYear')}</span>
            </div>
        );
    }, [licenseType, isDemo, t]);

    const onConfirmCancel = async () => {
        setCancelling(true);
        try {
            const updated = await userService.cancelSubscription();
            setSub(updated);
            toast.success(t('pages.subscription.cancel.toastSuccess'));
            setCancelOpen(false);
        } catch (e: any) {
            toast.error(getErr(e));
        } finally {
            setCancelling(false);
        }
    };

    const onConfirmResume = async () => {
        if (!sub?.cancelAtPeriodEnd) {
            toast.message('Work in progress...');
            return;
        }
        setCancelling(true);
        try {
            const updated = await userService.resumeSubscription();
            setSub(updated);
            toast.success(t('pages.subscription.cancel.toastSuccessResume'));
            setCancelOpen(false);
        } catch (e: any) {
            toast.error(getErr(e));
        } finally {
            setCancelling(false);
        }
    };

    const showCancel = !!sub && !sub.cancelAtPeriodEnd && !isDemo;

    return (
        <div id="subscriptionPage" className="subscriptionPage">
            <BreadCrumbs
                breadcrumbs={[
                    { to: '/', name: t('search') },
                    { to: '/subscription', name: t('components.navbar.subscription') },
                ]}
            />

            <div className="card">
                <div className="pageTitle">{t('pages.subscription.title')}</div>

                {loading ? (
                    <div className="stateText">{t('pages.common.loading')}</div>
                ) : !sub ? (
                    <div className="stateText">{t('subscription.empty')}</div>
                ) : isDemo ? (
                    /* ── Demo user state ── */
                    <>
                        <div className="subscriptionBox">
                            <div className="boxHeader">
                                <div className="planTitle">{planTitle}</div>
                                {!!planDescription && <div className="planDesc">{planDescription}</div>}
                            </div>

                            {!!formattedEndDate && (
                                <div className="boxRow">
                                    <div className="rowLabel">
                                        {t('pages.subscription.fields.endDateSubscription', {
                                            defaultValue: 'Einddatum',
                                        })}
                                    </div>
                                    <div className="rowValue">{formattedEndDate}</div>
                                </div>
                            )}
                        </div>

                        <div className="buttons">
                            <button
                                type="button"
                                className="button-sub button-ready"
                                onClick={() => history.push('/license#enterprise')}
                            >
                                {t('pages.subscription.demo.ready', { defaultValue: 'Neem contact op' })}
                            </button>
                            <button
                                type="button"
                                className="button-sub button-ready"
                                onClick={() => history.push('/license')}
                            >
                                {t('pages.subscription.demo.choosePlan', { defaultValue: 'Kies abonnement' })}
                            </button>
                        </div>
                    </>
                ) : (
                    /* ── Active subscription state ── */
                    <>
                        <div className="subscriptionBox">
                            <div className="boxHeader">
                                <div className="planTitle">{planTitle}</div>
                                {!!planDescription && <div className="planDesc">{planDescription}</div>}
                            </div>

                            <div className="boxRow">
                                <div className="rowLabel">{t('pages.subscription.fields.amount')}</div>
                                <div className="rowValue">{priceNode}</div>
                            </div>

                            <div className="boxRow">
                                <div className="rowLabel">
                                    {sub.cancelAtPeriodEnd
                                        ? t('pages.subscription.fields.endDateSubscription')
                                        : t('pages.subscription.fields.nextRenewal')}
                                </div>
                                <div className="rowValue">{formattedEndDate}</div>
                            </div>
                        </div>

                        <div className="buttons">
                            <button
                                type="button"
                                className="button-sub button-ready"
                                onClick={() => (onConfirmResume(), setCancelOpen(false))}
                            >
                                {sub.cancelAtPeriodEnd
                                    ? t('pages.subscription.choose')
                                    : t('pages.subscription.change')}
                            </button>

                            {!sub.cancelAtPeriodEnd && (
                                <button
                                    type="button"
                                    className="button-sub button-outline"
                                    disabled={!showCancel || cancelling}
                                    onClick={() => setCancelOpen(true)}
                                >
                                    {t('pages.subscription.cancel.button')}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <CancelSubscriptionModal
                open={cancelOpen}
                loading={cancelling}
                onClose={() => setCancelOpen(false)}
                onConfirm={onConfirmCancel}
            />
        </div>
    );
};

export default SubscriptionPage;
